/**
 * Scroll the minimum distance needed to bring a just-opened overlay fully into
 * view — the vertical half of the placement problem the filter popovers already
 * solve horizontally.
 *
 * Every Pebbl filter menu is a `position: absolute` box hanging off the bottom
 * of its trigger (`.dd-menu`, `.pop`, the specifier panel). Opened from a filter
 * panel sitting low in the viewport — the normal state on a tablet, where the
 * panel wraps to five or six rows — the menu extends past the bottom edge and the
 * user has to scroll by hand to reach the options they opened it for. Nothing
 * clips it, so the bottom overflow is scrollable; the page just never moves.
 *
 * The overlay is NOT repositioned: it stays exactly where its CSS puts it,
 * visually attached to its trigger, and the scrollport moves under it instead.
 * That keeps one placement rule for every viewport (no flipped variant to style,
 * no tablet-only offsets) and is why this is a scroll helper rather than a
 * positioning one.
 */

export interface RevealOptions {
  /** Breathing room kept between the overlay and the visible edge. */
  gap?: number;
  /**
   * Height of the fixed/sticky chrome covering the top of the viewport. Defaults
   * to the app header's measured height — see {@link stickyHeaderInset}.
   */
  top?: number;
}

/**
 * How far outside the band counts as "still inside it".
 *
 * Rects and band edges are fractional, and the browser snaps a scroll offset to
 * its own device-pixel grid — so a pass that lands a box exactly on an edge can
 * leave it a fraction of a pixel outside. Read literally that is a covered edge,
 * and the answer to a covered edge is a whole `gap` of correction: a box that was
 * already fully on screen moved the page 12px, and 12px back, every time it was
 * revealed again. Under a pixel nothing is clipped and nothing is worth moving
 * for, so the pass settles instead of ringing. A genuine overflow is orders of
 * magnitude larger than this and is unaffected.
 */
const EDGE_SLACK = 1;

/** Overflow values that make a box its own scrollport. */
const SCROLLABLE = /^(auto|scroll|overlay)$/;

/**
 * Bring `el` fully into view, scrolling as little as possible.
 *
 * No-op when it is already visible, so it is safe to call on every placement
 * pass (open, mode change, resize) — the correction converges to zero rather
 * than nudging the page each time.
 */
export function revealInScrollport(el: HTMLElement, options: RevealOptions = {}): void {
  const gap = options.gap ?? 12;
  const top = options.top ?? stickyHeaderInset();

  // Innermost scrollport first, then outwards: revealing the overlay inside a
  // nested scroller can still leave that scroller's own box off-screen, and each
  // step re-measures, so the outer pass corrects whatever the inner one could not.
  for (const scroller of scrollChain(el)) {
    const delta = requiredScroll(el, visibleBand(scroller, top), gap);
    if (!delta) {
      continue;
    }
    if (scroller) {
      scroller.scrollTop += delta;      // clamped by the browser
    } else {
      // `behavior: 'auto'` on purpose: nothing else in the console animates a
      // scroll made in response to opening a menu, and an animation would leave
      // the options moving under the pointer for a few hundred ms after the
      // click that opened them.
      window.scrollBy({top: delta, behavior: 'auto'});
    }
  }
}

/**
 * How far the page's sticky chrome reaches down the viewport — the top of the
 * usable band. Measured rather than hardcoded: `<app-header>` is the sticky
 * element (see its component CSS) and it is not the same height at every width.
 */
export function stickyHeaderInset(): number {
  const header = document.querySelector<HTMLElement>('app-header');
  if (!header) {
    return 0;
  }
  const position = getComputedStyle(header).position;
  if (position !== 'sticky' && position !== 'fixed') {
    return 0;
  }
  // While stuck, `bottom` IS the height; scrolled to the top of a page it is the
  // same number. Negative once a non-stuck header has scrolled away.
  return Math.max(0, header.getBoundingClientRect().bottom);
}

/**
 * The scrollports that can move `el`, innermost first, ending with the page
 * itself (`null`).
 *
 * The page link is always included: `documentElement` reports
 * `overflow-y: visible`, so it never passes the test below, and on this app it is
 * usually the only thing that scrolls at all.
 */
function scrollChain(el: HTMLElement): Array<HTMLElement | null> {
  const chain: Array<HTMLElement | null> = [];
  for (let node = el.parentElement; node; node = node.parentElement) {
    if (node === document.body || node === document.documentElement) {
      continue;
    }
    const style = getComputedStyle(node);
    if (SCROLLABLE.test(style.overflowY) && node.scrollHeight > node.clientHeight + 1) {
      chain.push(node);
    }
  }
  chain.push(null);
  return chain;
}

/**
 * Vertical span of a scrollport that is actually on screen.
 *
 * "On screen" means the VISUAL viewport, not the layout one. On iOS Safari the
 * virtual keyboard shrinks only the visual viewport — `documentElement.clientHeight`
 * is unchanged — so measuring against the layout viewport aims at a band that
 * includes the area behind the keyboard, and a focused input inside a just-opened
 * menu is "revealed" into a strip the user cannot see. Measured on a 390x664 phone
 * with a 300px keyboard: the correction came out the same as with no keyboard at
 * all, leaving one row of options above it.
 */
function visibleBand(scroller: HTMLElement | null, top: number): { top: number, bottom: number } {
  const {top: viewTop, bottom: viewBottom} = visualBand();
  if (!scroller) {
    return {top: Math.max(top, viewTop), bottom: viewBottom};
  }
  const rect = scroller.getBoundingClientRect();
  return {top: Math.max(rect.top, top, viewTop), bottom: Math.min(rect.bottom, viewBottom)};
}

/**
 * The part of the layout viewport the user can actually see, in client (
 * `getBoundingClientRect`) coordinates.
 *
 * `visualViewport.offsetTop` is where the visual viewport starts inside the layout
 * viewport, which is the same origin rects are measured from — so the band is
 * simply that offset plus its height. Both bounds stay clamped by
 * `documentElement.clientHeight`: unlike `visualViewport.height` it excludes a
 * horizontal scrollbar, which covers the bottom of the viewport on the listings wide
 * enough to have one. Without the API (and on desktop, where the two agree) this
 * returns exactly what the layout viewport did before.
 */
function visualBand(): { top: number, bottom: number } {
  const layout = document.documentElement.clientHeight;
  const vv = window.visualViewport;
  if (!vv) {
    return {top: 0, bottom: layout};
  }
  return {
    top: Math.max(0, vv.offsetTop),
    bottom: Math.min(layout, vv.offsetTop + vv.height)
  };
}

/**
 * Signed scroll delta that reveals `el` inside `band`, or 0 when it already is.
 *
 * Uncovering the TOP takes the trigger with it whenever both fit in the band —
 * for an OVERLAY `el.offsetParent` is the positioned box it hangs off, i.e. the
 * filter control itself — so a page scrolled past its filter panel does not end
 * up showing the menu with the control it belongs to hidden under the header.
 * See {@link triggerOf} for why an in-flow box has no trigger to take.
 *
 * Bottom overflow is corrected only as far as the overlay's own top edge, and the
 * top case wins when both edges are out. Either way the overlay's own top is the
 * last thing given up: an overlay taller than the band has to lose an edge, and a
 * lost tail (which its internal scrolling or the page can still reach) beats a
 * lost head, which reads as a menu opening off the top of the screen.
 */
function requiredScroll(el: HTMLElement, band: { top: number, bottom: number }, gap: number): number {
  const rect = el.getBoundingClientRect();
  const anchor = triggerOf(el);
  const withTrigger = anchor ? Math.min(anchor.top, rect.top) : rect.top;
  const revealTop = rect.bottom - withTrigger <= band.bottom - band.top ? withTrigger : rect.top;

  const overTop = band.top - revealTop;
  if (overTop > EDGE_SLACK) {
    return -Math.round(overTop + gap);
  }
  const overBottom = rect.bottom - band.bottom;
  if (overBottom > EDGE_SLACK) {
    // FLOOR, not round. Rects and band edges are fractional, and rounding the
    // clamp UP overshoots by up to half a pixel — which lands the box's top just
    // ABOVE the band, invisible in itself but not harmless: the next pass reads
    // that half pixel as a covered head and answers it with a full `gap` of
    // correction. Revealing a box that was already fully on screen then nudged
    // the page 12px, which is exactly what "already visible: do not scroll" is
    // there to prevent. Flooring gives up at most 1px of the bottom gutter and
    // can never carry the top past the edge, so a second pass returns 0.
    return Math.floor(Math.min(overBottom + gap, rect.top - band.top));
  }
  return 0;
}

/**
 * The control an OVERLAY hangs off, or `null` for a box that is simply in flow.
 *
 * `offsetParent` only names a trigger when the element is taken OUT of flow to sit
 * against it — every menu and popover that reaches here is `position: absolute`
 * under its own control, so its offsetParent IS that control. An in-flow box's
 * offsetParent is merely the nearest positioned ancestor, which is usually some
 * card or section far above it and has nothing to do with what opened it. Reading
 * it as a trigger there is not a near miss, it is a different element: revealing
 * an inline note composer dragged the page up to the top of the whole notes card,
 * moving the reader hundreds of pixels — and moving them at all when the composer
 * was already fully on screen, which is the one thing this must never do.
 *
 * Every existing caller is `position: absolute`, so this gate changes nothing for
 * them; it only stops the heuristic being applied where it means nothing.
 */
function triggerOf(el: HTMLElement): DOMRect | null {
  const position = getComputedStyle(el).position;
  if (position !== 'absolute' && position !== 'fixed') {
    return null;
  }
  return el.offsetParent instanceof HTMLElement ? el.offsetParent.getBoundingClientRect() : null;
}
